import type { CachedContact, SauceRating, SauceType, ScanRecord } from "./types";

const DEVICE_KEY = "gus.deviceId";
const SCANS_KEY = "gus.scans";
const CONTACT_KEY = "gus.contact";
const WELCOME_KEY = "gus.welcomeSeen";
const SESSION_KEY = "gus.sessionSubmitted";
const BRAND_KEY = "gus.brandSubmitted";

function uuid(): string {
  return crypto.randomUUID();
}

export function getOrCreateDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_KEY);
    if (existing) return existing;
    const id = uuid();
    localStorage.setItem(DEVICE_KEY, id);
    return id;
  } catch {
    // localStorage unavailable (private mode quirks); fall back to in-memory id
    return uuid();
  }
}

export function getScans(): ScanRecord[] {
  try {
    const raw = localStorage.getItem(SCANS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function hasScanned(eventSlug: string, sauceType: SauceType): boolean {
  return getScans().some((s) => s.eventSlug === eventSlug && s.sauceType === sauceType);
}

export function getRatedSauces(eventSlug: string): Set<SauceType> {
  const set = new Set<SauceType>();
  for (const s of getScans()) {
    if (s.eventSlug === eventSlug) set.add(s.sauceType);
  }
  return set;
}

export function recordScan(
  eventSlug: string,
  sauceType: SauceType,
  sauceRating: SauceRating,
): void {
  try {
    const scans = getScans();
    scans.push({
      eventSlug,
      sauceType,
      sauceRating,
      ratedAt: new Date().toISOString(),
    });
    localStorage.setItem(SCANS_KEY, JSON.stringify(scans));
  } catch {
    // best-effort; silent failure is fine
  }
}

export function loadContact(): CachedContact | null {
  try {
    const raw = localStorage.getItem(CONTACT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedContact>;
    return {
      name: typeof parsed.name === "string" ? parsed.name : "",
      email: typeof parsed.email === "string" ? parsed.email : "",
      phone: typeof parsed.phone === "string" ? parsed.phone : "",
      zip: typeof parsed.zip === "string" ? parsed.zip : "",
      marketingOptIn: parsed.marketingOptIn !== false,
    };
  } catch {
    return null;
  }
}

export function saveContact(contact: CachedContact): void {
  try {
    localStorage.setItem(CONTACT_KEY, JSON.stringify(contact));
  } catch {
    // best-effort
  }
}

// Welcome screen is shown once per event. Skipping counts as seen so partial
// raters don't get re-prompted on every bottle. Cleared localStorage shows
// the welcome again — acceptable edge case.
export function hasSeenWelcome(eventSlug: string): boolean {
  try {
    const raw = localStorage.getItem(WELCOME_KEY);
    if (!raw) return false;
    const seen = JSON.parse(raw) as string[];
    return Array.isArray(seen) && seen.includes(eventSlug);
  } catch {
    return false;
  }
}

export function markWelcomeSeen(eventSlug: string): void {
  try {
    const raw = localStorage.getItem(WELCOME_KEY);
    const seen = raw ? (JSON.parse(raw) as string[]) : [];
    if (!seen.includes(eventSlug)) {
      seen.push(eventSlug);
      localStorage.setItem(WELCOME_KEY, JSON.stringify(seen));
    }
  } catch {
    // best-effort
  }
}

// Session-row creation flag, per event. Set after a successful
// submitSession() call from Welcome or WrapUp. Used by WrapUp to decide
// whether to show the contact form — a stale `gus.contact` localStorage
// object isn't a reliable signal of "we have consent for THIS event."
export function hasSubmittedSession(eventSlug: string): boolean {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return false;
    const seen = JSON.parse(raw) as string[];
    return Array.isArray(seen) && seen.includes(eventSlug);
  } catch {
    return false;
  }
}

export function markSessionSubmitted(eventSlug: string): void {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    const seen = raw ? (JSON.parse(raw) as string[]) : [];
    if (!seen.includes(eventSlug)) {
      seen.push(eventSlug);
      localStorage.setItem(SESSION_KEY, JSON.stringify(seen));
    }
  } catch {
    // best-effort
  }
}

// Brand vibe is submitted once per (device, event) on the wrap-up. If they
// come back after all 4, no need to ask again.
export function hasSubmittedBrand(eventSlug: string): boolean {
  try {
    const raw = localStorage.getItem(BRAND_KEY);
    if (!raw) return false;
    const seen = JSON.parse(raw) as string[];
    return Array.isArray(seen) && seen.includes(eventSlug);
  } catch {
    return false;
  }
}

export function markBrandSubmitted(eventSlug: string): void {
  try {
    const raw = localStorage.getItem(BRAND_KEY);
    const seen = raw ? (JSON.parse(raw) as string[]) : [];
    if (!seen.includes(eventSlug)) {
      seen.push(eventSlug);
      localStorage.setItem(BRAND_KEY, JSON.stringify(seen));
    }
  } catch {
    // best-effort
  }
}
